import { AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

interface ProblemiSequenzaProps {
  /** Null finche' il controllo non e' ancora arrivato. */
  problemi: string[] | null;
}

/** Lo stesso avviso ripetuto diventa una riga sola con il conteggio. */
function raggruppa(problemi: string[]): { testo: string; volte: number }[] {
  const conteggio = new Map<string, number>();
  for (const p of problemi) conteggio.set(p, (conteggio.get(p) ?? 0) + 1);
  return [...conteggio].map(([testo, volte]) => ({ testo, volte }));
}

/**
 * Cosa manca per accendere, chiuso in un bottone nella testata.
 *
 * L'elenco completo si apre solo se lo chiedi: il dettaglio e' gia' scritto
 * in rosso sulle caselle, qui serve sapere quante cose mancano.
 */
export function ProblemiSequenza({ problemi }: ProblemiSequenzaProps) {
  if (problemi === null) return null;
  if (problemi.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium text-green-700 bg-green-50 border border-green-200">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Pronta da accendere
      </span>
    );
  }

  const righe = raggruppa(problemi);
  const quante = problemi.length === 1 ? '1 cosa da sistemare' : `${problemi.length} cose da sistemare`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium text-amber-900 bg-amber-50 border border-amber-300 hover:bg-amber-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        >
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
          {quante}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 rounded-none">
        <div className="px-3 py-2.5 border-b border-neutral-100">
          <p className="text-xs font-semibold text-neutral-900">Prima di accendere</p>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Le caselle con la riga rossa in fondo ti dicono cosa manca.
          </p>
        </div>
        <ul className="py-1.5 max-h-72 overflow-y-auto">
          {righe.map(({ testo, volte }) => (
            <li key={testo} className="flex items-start gap-2 px-3 py-1.5 text-xs text-neutral-700">
              <span className="w-1.5 h-1.5 mt-1.5 flex-shrink-0 bg-amber-500" />
              <span className="flex-1">{testo}</span>
              {volte > 1 && (
                <span className="text-[10px] font-semibold text-neutral-500 bg-neutral-100 px-1.5 py-0.5">
                  x{volte}
                </span>
              )}
            </li>
          ))}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
